'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useChickenTick, useClaimChicken } from '../queries/useChickenQueries';
import { RESPAWN_COOLDOWN_MS, shouldShowSpawn } from './chickenSpawnLogic';

interface ChickenSpawn {
  spawnId: string | null;
  onClaim: () => void;
  onDespawn: () => void;
}

export function useChickenSpawn(): ChickenSpawn {
  const { data, dataUpdatedAt } = useChickenTick();
  const claim = useClaimChicken();

  const tickSpawnId = data?.spawnId ?? null;

  const [shownSpawnId, setShownSpawnId] = useState<string | null>(null);
  const claimedRef = useRef<Set<string>>(new Set());
  const cooldownUntilRef = useRef(0);

  useEffect(() => {
    if (
      shouldShowSpawn({
        tickSpawnId,
        shownSpawnId,
        claimed: claimedRef.current,
        cooldownUntil: cooldownUntilRef.current,
        now: Date.now(),
      })
    ) {
      setShownSpawnId(tickSpawnId);
    }
  }, [tickSpawnId, shownSpawnId, dataUpdatedAt]);

  const onClaim = useCallback(() => {
    if (shownSpawnId) {
      claimedRef.current.add(shownSpawnId);
      claim.mutate(shownSpawnId);
    }
  }, [claim, shownSpawnId]);

  const onDespawn = useCallback(() => {
    cooldownUntilRef.current = Date.now() + RESPAWN_COOLDOWN_MS;
    setShownSpawnId(null);
  }, []);

  return { spawnId: shownSpawnId, onClaim, onDespawn };
}
